import { CurrencyPipe } from '@angular/common'
import { Component, DestroyRef, inject, input, OnInit, signal, TemplateRef, ViewChild } from '@angular/core'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import { FormControl, FormGroup, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms'
import { Router, RouterLink } from '@angular/router'
import { NgSelectModule } from '@ng-select/ng-select'
import { PostcodeLookupService } from 'src/app/core/services/postcode-lookup.service'
import { InputComponent } from 'src/app/shared/components/form-elements/input/input.component'
import { LabelComponent } from 'src/app/shared/components/form-elements/label/label.component'
import { PostcodeAddress } from 'src/app/shared/interfaces/postcode-lookup/postcode-address.interface'
import { UKPostcodeValidator } from 'src/app/shared/validators/postcode.validator'
import { RegisterService } from './services/register.service'
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome'
import { userNameAvailabilityValidator } from 'src/app/shared/validators/username-availability.validator'
import { emailAvailabilityValidator } from 'src/app/shared/validators/email-availability.validator'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { CanDeactivateComponent } from 'src/app/core/guards/unsaved-changes.guard'
import { ToastrService } from 'ngx-toastr'
import { StaffService } from 'src/app/core/services/staff/staff.service'
import { environment } from 'src/environments/environment'
import { LoadingSpinnerComponent } from 'src/app/shared/components/loading-spinner/loading-spinner.component'
import { AuthService } from '@auth0/auth0-angular'
import { PostcodeLookupComponent } from 'src/app/shared/components/form-elements/postcode-lookup/postcode-lookup.component'

@Component({
    selector: 'sf-register',
    imports: [
        RouterLink,
        ReactiveFormsModule,
        FontAwesomeModule,
        NgSelectModule,
        LabelComponent,
        InputComponent,
        LoadingSpinnerComponent,
        PostcodeLookupComponent,
        CurrencyPipe,
    ],
    templateUrl: './register.component.html',
    styleUrl: './register.component.scss',
})
export class RegisterComponent extends CanDeactivateComponent implements OnInit {
    #registerService = inject(RegisterService)
    #fb = inject(NonNullableFormBuilder)
    #postcodeService = inject(PostcodeLookupService)
    #router = inject(Router)
    #toastr = inject(ToastrService)
    #destroyRef = inject(DestroyRef)
    #staffService = inject(StaffService)
    #authService = inject(AuthService)
    modalService = inject(NgbModal)

    @ViewChild('successModal') registerModal!: TemplateRef<any>

    apiUrl = environment.baseUrl

    code = input<string | null>(null)
    state = input<string | null>(null)

    hasDataFromAccountsIntegrator = signal<boolean>(false)
    accountsIntegratorName = signal<string | null>(null)

    packages = toSignal(this.#registerService.getPackages(), { initialValue: [] })

    isLoading = signal<boolean>(false)
    year = new Date().getFullYear()

    registerForm: RegistrationForm = this.#fb.group({
        selectedPackageId: this.#fb.control<number | null>(null, [Validators.required]),
        username: this.#fb.control<string>(
            '',
            [Validators.required, Validators.minLength(5), Validators.maxLength(50)],
            [userNameAvailabilityValidator(this.#staffService)]
        ),
        email: this.#fb.control<string>(
            '',
            [Validators.required, Validators.email],
            [emailAvailabilityValidator(this.#staffService)]
        ),
        firstName: this.#fb.control<string>('', [Validators.required, Validators.maxLength(50)]),
        lastName: this.#fb.control<string>('', [Validators.required, Validators.maxLength(50)]),
        companyName: this.#fb.control<string>('', [Validators.required]),
        companySms: this.#fb.control<string>('', [
            Validators.required,
            Validators.maxLength(11),
            Validators.pattern('^\\S*$'),
        ]),
        phoneNumber: this.#fb.control<string>('', [Validators.required]),
        webAddress: this.#fb.control<string>('', [Validators.maxLength(255)]),
        wukNumber: this.#fb.control<string>('', [Validators.maxLength(10)]),
        wukTermsAccepted: this.#fb.control<boolean>(false),
        address: this.#fb.group({
            addressLine1: this.#fb.control<string>('', [Validators.required, Validators.maxLength(255)]),
            addressLine2: this.#fb.control<string>('', [Validators.maxLength(255)]),
            addressLine3: this.#fb.control<string>('', [Validators.maxLength(255)]),
            addressLine4: this.#fb.control<string>('', [Validators.maxLength(255)]),
            locality: this.#fb.control<string>('', [Validators.maxLength(255)]),
            town: this.#fb.control<string>('', [Validators.required, Validators.maxLength(255)]),
            county: this.#fb.control<string>('', [Validators.maxLength(255)]),
            postcode: this.#fb.control<string>('', [Validators.required, UKPostcodeValidator]),
        }),
        vatRegistered: this.#fb.control<boolean>(false),
        vatNumber: this.#fb.control<string>('', Validators.maxLength(50)),
        promoCode: this.#fb.control<string>('', [Validators.maxLength(50)]),
    })

    addressSuggestions = signal<PostcodeAddress[]>([])
    addressError = signal<string>('')
    gotAddress = signal<boolean>(false)

    wukNumber = toSignal(this.registerForm.controls.wukNumber.valueChanges, { initialValue: '' })

    ngOnInit(): void {
        this.registerForm.controls.vatRegistered.valueChanges
            .pipe(takeUntilDestroyed(this.#destroyRef))
            .subscribe((value) => {
                if (value) {
                    this.registerForm.controls.vatNumber.setValidators([Validators.required])
                } else {
                    this.registerForm.controls.vatNumber.clearValidators()
                }
                this.registerForm.controls.vatNumber.updateValueAndValidity()
            })

        this.registerForm.controls.wukNumber.valueChanges
            .pipe(takeUntilDestroyed(this.#destroyRef))
            .subscribe((value) => {
                if (value) {
                    this.registerForm.controls.wukTermsAccepted.setValidators([Validators.requiredTrue])
                } else {
                    this.registerForm.controls.wukTermsAccepted.clearValidators()
                }
                this.registerForm.controls.wukTermsAccepted.updateValueAndValidity()
            })

        if (this.code() && this.state()) {
            this.isLoading.set(true)
            this.#registerService.xeroCallback(this.code()!, this.state()!).subscribe({
                next: (xeroPreRegistration) => {
                    this.hasDataFromAccountsIntegrator.set(true)
                    this.accountsIntegratorName.set(
                        `${xeroPreRegistration.firstName} ${xeroPreRegistration.lastName}`.trim()
                    )

                    this.registerForm.patchValue({
                        companyName: xeroPreRegistration.address.companyName || '',
                        email: xeroPreRegistration.email,
                        firstName: xeroPreRegistration.firstName,
                        lastName: xeroPreRegistration.lastName,
                        username: xeroPreRegistration.username,
                        phoneNumber: xeroPreRegistration.phoneNumber,
                        promoCode: xeroPreRegistration.promoCode || '',
                        address: {
                            addressLine1: xeroPreRegistration.address.addressLine1,
                            addressLine2: xeroPreRegistration.address.addressLine2,
                            addressLine3: xeroPreRegistration.address.addressLine3,
                            addressLine4: xeroPreRegistration.address.addressLine4,
                            locality: xeroPreRegistration.address.locality,
                            town: xeroPreRegistration.address.town,
                            county: xeroPreRegistration.address.county,
                            postcode: xeroPreRegistration.address.postcode,
                        },
                    })

                    this.registerForm.controls.promoCode.disable()
                    this.registerForm.markAllAsTouched()
                },
                error: () => {
                    this.#toastr.error('Failed to retrieve information from Xero.', 'Error', {
                        timeOut: 20_000,
                    })
                },
                complete: () => {
                    // remove all query parameters from the URL
                    this.#router.navigate([], {
                        queryParams: {},
                        replaceUrl: true,
                    })

                    this.isLoading.set(false)
                },
            })
        }
    }

    findAddresses(): void {
        this.addressError.set('')

        const postcodeControl = this.registerForm.controls.address.controls.postcode
        if (postcodeControl.invalid) {
            this.addressError.set('Please enter a valid postcode')
            return
        }

        this.isLoading.set(true)

        this.#postcodeService.getSuggestionsForPostcode(postcodeControl.value).subscribe({
            next: (addresses: PostcodeAddress[]) => {
                if (addresses.length === 0) {
                    this.addressError.set('No addresses found for this postcode, please try again')
                    return
                }
                this.addressSuggestions.set(addresses)
                this.isLoading.set(false)
            },
            error: () => {
                this.addressError.set('Failed to find address, please try again')
                this.isLoading.set(false)
            },
        })
    }

    updateAddress(address: PostcodeAddress): void {
        if (!address) {
            return
        }

        this.gotAddress.set(true)

        this.registerForm.controls.address.patchValue({
            addressLine1: address.envelopeAddress.line1,
            addressLine2: address.envelopeAddress.line2,
            town: address.envelopeAddress.town,
        })
    }

    submit(): void {
        this.registerForm.markAllAsTouched()

        if (this.registerForm.invalid) {
            this.#toastr.error('Please check the form for errors and try again.', 'Error')
            return
        }

        this.isLoading.set(true)
        this.#registerService.register(this.registerForm.getRawValue()).subscribe({
            next: () => {
                this.registerForm.reset()
                this.registerForm.markAsUntouched()

                this.#toastr.success(
                    'Registration successful, please check your email for further instructions.',
                    'Success'
                )

                this.modalService
                    .open(this.registerModal, { size: 'md', fullscreen: 'md' })
                    .hidden.pipe(takeUntilDestroyed(this.#destroyRef))
                    .subscribe({
                        next: () => {
                            this.#router.navigate(['/'])
                        },
                    })
            },
            error: () => {
                this.#toastr.error('Failed to register, please try again.', 'Error')
                this.isLoading.set(false)
            },
            complete: () => {
                this.isLoading.set(false)
            },
        })
    }

    openModal(content: TemplateRef<any>): void {
        this.modalService.open(content, { size: 'xl', fullscreen: 'md' })
    }

    public override canDeactivate(): boolean {
        return !(this.registerForm.dirty && this.registerForm.touched)
    }
}

interface RegistrationForm
    extends FormGroup<{
        selectedPackageId: FormControl<number | null>
        companyName: FormControl<string>
        companySms: FormControl<string>
        username: FormControl<string>
        email: FormControl<string>
        firstName: FormControl<string>
        lastName: FormControl<string>
        webAddress: FormControl<string>
        wukNumber: FormControl<string>
        wukTermsAccepted: FormControl<boolean>
        vatRegistered: FormControl<boolean>
        vatNumber: FormControl<string>
        promoCode: FormControl<string>
        address: FormGroup<{
            addressLine1: FormControl<string>
            addressLine2: FormControl<string>
            addressLine3: FormControl<string>
            addressLine4: FormControl<string>
            locality: FormControl<string>
            town: FormControl<string>
            county: FormControl<string>
            postcode: FormControl<string>
        }>
        phoneNumber: FormControl<string>
    }> {}
